import { useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { isEmpty, map } from "lodash"
import { CardContent, Paper, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material"
import { DATE } from "helpers/shapes"
import { numberFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import { computeCSVData } from "helpers/csv"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import { THRESHOLD_PARAMS } from "helpers/threshold"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import DetailRow from "./components/detail-row"
import NotEnoughHistoricalFactorSensitivityData from "components/not-enough-historical-factor-sensitivity-data"

import "./style.scss"

export default function SecurityProfileFactorSensitivity({
  setDate,
  selectedDate,
  firstDate,
  startDate,
  endDate,
  items,
  displayType,
  setDisplayType,
  options,
  toggle,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  data,
  details,
  openFactorProfile,
  toggleOnly,
  only,
  fileName,
  trackCSVDownload,
  factorSensitivityType,
  isOpPro,
  thresholdParams,
  selectPredictedFactorSensitivity,
  resetDate,
  isPeriodIntraday,
}) {
  const makeTrackedAction = useCallback(fn => (...args) => { fn(...args, factorSensitivityType) }, [factorSensitivityType])

  useEffect(() => {
    const adjustedStartDate = firstDate || startDate

    if (selectedDate && (selectedDate < adjustedStartDate)) {
      resetDate()
    }
  }, [firstDate, startDate, selectedDate, resetDate])

  function onTabChanged(_, id) {
    setDisplayType(id, factorSensitivityType)
  }

  let content
  if (isEmpty(data) && factorSensitivityType === FACTOR_SENSITIVITY_TYPES.HISTORICAL) {
    content = <NotEnoughHistoricalFactorSensitivityData {...{
      assetTypeName: "security",
      isOpPro,
      startDate,
      handleSelectPredicted: selectPredictedFactorSensitivity,
      displayChangeDateMsg: false,
    }}
    />
  } else {
    content = <CardContent className="modules_security-profile-factor-sensitivity card-content">
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
            value: displayType,
            onTabChanged,
            tabs: map(options, ({ id, label, value }) => ({
              key: id,
              value: id,
              label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
            })),
            actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackCSVDownload} />],
          }}
          />
          <MultiLine
            tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} isOneDayMode={isPeriodIntraday} />}
            {...{
              startDate,
              date: selectedDate,
              setDate,
              endDate,
              data,
              onlyItem: only,
              items,
              toggle: makeTrackedAction(toggle),
              focusChartLine,
              blurChartLine,
              focusedChartLine,
              toggleOnly: makeTrackedAction(toggleOnly),
              thresholdParams,
            }}
          />
        </div>
        <div className="details">
          <h4 className="header">As of {formatLongDate(selectedDate)}</h4>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell colSpan={2} align="right">1 STD Factor Impact (%)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(details, (value, index) => {
                if (displayType === "total" && index === 0) {
                  return null
                } else {
                  return <DetailRow {...{
                    key: value.id,
                    value,
                    index,
                    openFactorProfile,
                    thresholdParams,
                  }}
                  />
                }
              })}
            </TableBody>
          </Table>
        </div>
      </Paper>
    </CardContent>
  }

  return content
}

SecurityProfileFactorSensitivity.propTypes = {
  startDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  displayType: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  setDisplayType: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  firstDate: PropTypes.string,
  resetDate: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  details: PropTypes.array.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  only: PropTypes.string,
  trackCSVDownload: PropTypes.func.isRequired,
  fileName: PropTypes.string.isRequired,
  factorSensitivityType: PropTypes.string.isRequired,
  isPeriodIntraday: PropTypes.bool,
  isOpPro: PropTypes.bool.isRequired,
  thresholdParams: THRESHOLD_PARAMS,
  selectPredictedFactorSensitivity: PropTypes.func.isRequired,
}
